/* =============================================================================
   WISHLIST PAGE — SHARED / PORTABLE enrichment layer for a Mst Wishlist Prime
   ("iWish") app-proxy page.

   phaser: shared — NO store-specific logic lives here. Copy this file
   verbatim. Store-specific behaviour is added by a second, later-loading
   module through window.CCWishlist.extend (see EXTENSION POINTS below). This
   file names no store, no product category and no collection: if you find
   yourself wanting to, that belongs in the extension module.

   THE "+" TRAP: the app decodes "+" as a SPACE when it stores its own JS box,
   which strips every concatenation and increment and leaves the file
   syntactically dead. That is why this is a theme asset. Leave the app's JS
   box EMPTY.

   WHY THIS FILE LOOKS PARANOID
   This page's scripts run before jQuery and long before a single product
   exists in the DOM. So: define hooks here, never query the DOM at top level,
   and re-run everything from window.iWishPageinitFn (the app's only real
   integration point) plus a MutationObserver backstop.

   THE THINGS THAT WILL BITE YOU IF YOU EDIT THIS
   1. The observer watches the grid, and sorting re-appends cards INTO the
      grid. Re-appending an attached node is a remove+insert and DOES emit
      records. If you mutate the grid without muting the observer you get a
      permanent 25Hz re-render loop. Every grid write goes through withMute().
   2. The app renders items ASYNCHRONOUSLY, after window load. Until it has, an
      empty grid means "not loaded yet", not "empty wishlist". Nothing may act
      on emptiness before appRendered is true.
   3. The last-visit timestamp is read ONCE at module init. Reading it live
      would make the "since you were last here" line erase itself on the second
      pass, because the first pass has already rewritten it.
   4. The page markup goes through Liquid, so a literal double-brace in a JS
      string could be eaten. Token markers are built with String.fromCharCode.
   5. Never write a product title into an attribute — titles carry bracketed
      handling prefixes and apostrophes. Titles are read from the DOM.
   6. The localStorage keys below are CUSTOMER DATA. Rename one and every
      returning shopper loses their unread state, their saved-on dates and
      their "since you were last here" line, permanently.
   7. NEVER boot from module scope. A classic `defer` script runs with
      readyState === 'interactive', never 'loading', so an `else { boot(); }`
      branch executes BEFORE the next defer'd script has even been fetched —
      i.e. before any extension has registered. Every latch this file sets
      would then be latched against an empty extension set. Boot is gated on
      DOMContentLoaded, which is guaranteed to be after every defer'd script.
      See BOOT at the bottom.

   EXTENSION POINTS — window.CCWishlist.extend(function (api) { ... })
     api.addStatCell({ id, label, order, value(ctx) })
     api.addMastheadLine({ id, order, render(ctx) })
     api.addBadges({ id, fn(card, product|null, state) -> [{label, tone}] })
     api.addFilter({ id, label, order, test(card) })
     api.filterVariants({ id, fn(variants, product, card) -> variants to SHOW })
     api.addEmptyLink({ href, label, order })
     api.setCopy(key, string)
     api.util.{ qs qsa on attr text warn guard store money root cards title
                variantTitle variantId priceCents isSoldOut fetchProduct }

   ORDER INDEPENDENCE (the contract, not an aspiration)
   - The namespace is published synchronously at parse, so an extension can
     always register — before us (via CCWishlist.q) or after us.
   - extend() is safe to call at ANY time. Called after the first render pass
     it invalidates the per-node latches it can (size switchers, empty-state
     links, the account sentence, the dispatch line) and schedules a debounced
     re-pass. Grid writes inside that pass still go through withMute(), so the
     observer cannot loop.
   - Registering the same function or the same id twice is a no-op. A
     duplicated <script src> therefore cannot double-apply a hook.

   Every hook call is wrapped. A hook that throws three times is retired for
   the rest of the page view. NOTHING an extension does can stop this page
   rendering — that is the one hard guarantee.
   ========================================================================== */
(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.document) { return; }

  var D = window.document;
  var TOKEN_OPEN = String.fromCharCode(123, 123); /* see header note 4 */
  var TAG_OPEN = String.fromCharCode(123, 37);

  /* The namespace is claimed FIRST, so an extension that somehow parses before
     us can still queue. defer preserves document order today; it is one editor
     mistake (async, or an inline tag) away from not doing so. */
  var NS = window.CCWishlist = window.CCWishlist || {};

  /* Re-entry guard. Two <script src> tags for this file — trivially reachable
     if the layout block is ever copied into a section during a port — would
     otherwise stand up a SECOND MutationObserver whose own `muting` flag knows
     nothing about the first instance's sort writes, and read them as app
     mutations. That is the permanent re-render loop, from the outside. */
  if (NS.__ccwLoaded) { return; }
  NS.__ccwLoaded = true;

  var pendingRegs = (NS.q && NS.q.length) ? NS.q.slice() : [];
  NS.q = null;
  NS.version = 1;

  /* ---------------------------------------------------------------- helpers */
  /* Silent by default — a shopper must never see our diagnostics. Add
     ?ccwdebug to the URL, or set localStorage['ccw:debug'], to escalate to
     console.warn while working on this file. Without that, a mistyped hook
     name produces literally no signal. */
  var DEBUG = false;
  try {
    DEBUG = /[?&]ccwdebug/.test(window.location.search || '') ||
            !!window.localStorage.getItem('ccw:debug');
  } catch (e) { DEBUG = false; }

  function qs(s, c) { try { return (c || D).querySelector(s); } catch (e) { return null; } }
  function qsa(s, c) {
    /* Note the explicit null check: a caller passing a missing ref must get
       nothing back, NOT a document-wide query. `c || D` alone silently widens
       the search to the whole page the moment a template element goes away. */
    if (arguments.length > 1 && !c) { return []; }
    try { return Array.prototype.slice.call((c || D).querySelectorAll(s)); } catch (e) { return []; }
  }
  function on(el, ev, fn, opt) { if (el && el.addEventListener) { el.addEventListener(ev, fn, opt); } }
  function isArray(v) { return Object.prototype.toString.call(v) === '[object Array]'; }
  function warn(e) {
    try {
      if (!window.console) { return; }
      if (DEBUG && window.console.warn) { window.console.warn('[ccw]', e); }
      else if (window.console.debug) { window.console.debug('[ccw]', e); }
    } catch (x) { /* console itself is the last thing that can fail */ }
  }
  function guard(fn) { return function () { try { return fn.apply(this, arguments); } catch (e) { warn(e); return null; } }; }

  /* THE hook wrapper. Three strikes and the hook is retired: 48 cards x 5
     passes is 240 throws from one bad extension, which would drown the console
     and stall the page. */
  function safe(fn, args, fallback) {
    if (typeof fn !== 'function' || fn.__ccwDead) { return fallback; }
    try { return fn.apply(null, args || []); }
    catch (e) {
      warn(e);
      fn.__ccwStrikes = (fn.__ccwStrikes || 0) + 1;
      if (fn.__ccwStrikes >= 3) { fn.__ccwDead = true; warn('extension hook retired after 3 throws'); }
      return fallback;
    }
  }

  /* A token that did not substitute comes back either empty or as the literal
     source. Treat both as missing rather than rendering the raw token. */
  function val(v) {
    if (typeof v !== 'string') { return ''; }
    v = v.trim();
    if (!v || v.indexOf(TOKEN_OPEN) !== -1 || v.indexOf(TAG_OPEN) !== -1) { return ''; }
    return v;
  }
  function attr(el, name) { return el && el.getAttribute ? val(el.getAttribute(name)) : ''; }

  var store = {
    get: function (k, fb) {
      try { var r = window.localStorage.getItem(k); return r === null ? fb : JSON.parse(r); }
      catch (e) { return fb; }
    },
    set: function (k, v) { try { window.localStorage.setItem(k, JSON.stringify(v)); } catch (e) { warn(e); } },
    sget: function (k, fb) {
      try { var r = window.sessionStorage.getItem(k); return r === null ? fb : JSON.parse(r); }
      catch (e) { return fb; }
    },
    sset: function (k, v) { try { window.sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) { warn(e); } }
  };

  /* See header note 6. Do not rename these. */
  var K_SNAP = 'ccw:snapshot';
  var K_SEEN = 'ccw:firstSeen';
  var K_SORT = 'ccw:sort';
  var K_VIEW = 'ccw:view';
  var K_VISIT = 'ccw:lastVisit';

  /* NOT customer data, deliberately unlike the five keys above. This is a pure
     id -> handle cache used to warm the product-JSON cache one page view
     early. Losing it costs a few hundred milliseconds on the next visit and
     nothing a shopper can perceive, so it is safe to rename, clear or drop. */
  var K_IDMAP = 'ccw:idmap';
  /* v2: entries are keyed MAP_KEY + id (see writeIdMap). A v1 map fails the
     version test on read and is discarded wholesale — no migration, the map
     rebuilds itself on the first enrichment. */
  var MAP_V = 2;
  /* A non-numeric prefix, and the whole reason it exists: a Shopify product id
     on an older catalogue is 9-10 digits (632910392), which is a canonical
     ARRAY INDEX. Object.keys hoists those ahead of every string key, in
     ascending numeric order, no matter what order they were inserted in — so
     without this prefix the insertion-order LRU below silently becomes
     eviction by id MAGNITUDE, and legacy small-id products pin themselves in
     the map forever while this visit's actual list gets truncated away. */
  var MAP_KEY = 'p';
  var MAP_MAX = 60;
  var MAP_TTL = 30 * 24 * 3600 * 1000;

  /* Read ONCE, before any pass can rewrite it. See header note 3. */
  var LAST_VISIT = store.get(K_VISIT, 0) || 0;
  var visitRecorded = false;

  var money = (function () {
    var fmt = null;
    /* en-AU / AUD are hardcoded. Any store outside AU has to change these two
       strings — the only locale assumption in this file. */
    try { fmt = new window.Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }); } catch (e) { fmt = null; }
    return function (cents) {
      var d = (cents || 0) / 100;
      if (fmt) { try { return fmt.format(d); } catch (e) { /* fall through */ } }
      return '$' + d.toFixed(2);
    };
  }());

  function text(el) { return el && el.textContent ? el.textContent.replace(/\s+/g, ' ').trim() : ''; }

  /* The app renders a multi-option variant as "First / Second". Everything
     shopper-facing on this page uses a middot instead, and the stylesheet
     uppercases it. One function, so the card's variant line, the size buttons
     and util.variantTitle cannot drift apart. */
  function prettyVariant(t) { return String(t || '').replace(/\s*\/\s*/g, ' · '); }

  /* ------------------------------------------------- extension state + copy */
  var EXT = { stats: [], lines: [], badges: [], variants: [], emptyLinks: [] };

  /* Every shopper-visible sentence that a store might want to word
     differently. Placeholders, not concatenation, so an override can reorder
     them. An unknown key is REFUSED — a silent typo would mean the shopper
     reads the generic noun forever.

     The four null-valued keys map onto nodes in the master template. null
     means "whatever the markup already says": the template ships store-neutral
     defaults, and this file only writes over them when a store has actually
     registered an override. That is what keeps the template copyable and the
     override path honest — no override, no write, no chance of clobbering
     hand-written markup with a generic string. */
  var COPY = {
    accountBody: '{n} · {v} tracked here. Sign in and your list follows you — phone, laptop, and the next time you clear your browser. We will flag price drops and restocks on what you have saved.',
    backOne: 'item came back in stock',
    backMany: 'items came back in stock',
    sizesNote: 'Changes what goes in the cart, not your saved size.',
    sizesUnavailable: 'Your saved option is no longer offered. The cart will use the option selected here.',
    deck: null,
    emptyTitle: null,
    emptyBody: null,
    printFoot: null
  };
  /* Which keys were explicitly overridden. Only these are written to markup. */
  var COPY_SET = {};

  /* Copy keys that own a node in the master template. Any key not listed here
     is consumed by JS at the point of use. */
  /* Prefer data-* hooks; fall back to the class names the master template has
     always shipped. Without the class fallback, setCopy(deck|empty*|printFoot)
     was a no-op when Wishlist Prime admin still had markup without data-ccw-*
     attrs. qs() returns the first match in document order — keep one node per
     key (data attr + class on the same element). */
  var COPY_NODES = {
    deck: '[data-ccw-deck], .ccw-deck',
    emptyTitle: '[data-ccw-empty-title], .ccw-empty__title',
    emptyBody: '[data-ccw-empty-body], .ccw-empty__body',
    printFoot: '[data-ccw-print-foot], .ccw-print-foot'
  };

  /* Fill {n}/{v}. A placeholder we cannot fill is removed ALONG WITH the
     middot that introduced it — "12 items · $0.00 tracked here" reads as a
     bug, and a bare " ·  tracked here" reads as broken markup. */
  function fillCopy(tpl, map) {
    var s = String(tpl || '');
    var keys = Object.keys(map);
    for (var i = 0; i < keys.length; i++) {
      var token = '{' + keys[i] + '}';
      var v = map[keys[i]];
      if (v) { s = s.split(token).join(v); }
      else {
        s = s.split(' · ' + token).join('')
             .split(token + ' · ').join('')
             .split(token).join('');
      }
    }
    return s.replace(/\s{2,}/g, ' ').trim();
  }

  /* --------------------------------------------------------------- elements */
  var R = {};
  function refs() {
    R.root = qs('[data-ccw-root]');
    if (!R.root) { return false; }
    R.grid = qs('[data-ccw-grid]', R.root);
    R.sys = qs('.sysMessage', R.root);
    R.toolbar = qs('[data-ccw-toolbar]', R.root);
    R.chips = qs('[data-ccw-chips]', R.root);
    R.sort = qs('[data-ccw-sort]', R.root);
    R.visible = qs('[data-ccw-visible]', R.root);
    R.empty = qs('[data-ccw-empty]', R.root);
    R.links = qs('[data-ccw-links]', R.root);
    R.recs = qs('[data-ccw-recs]', R.root);
    R.recsGrid = qs('[data-ccw-recs-grid]', R.root);
    R.bulk = qs('[data-ccw-bulk]', R.root);
    R.bulkSummary = qs('[data-ccw-bulk-summary]', R.root);
    R.toast = qs('[data-ccw-toast]', R.root);
    R.toastMsg = qs('[data-ccw-toast-msg]', R.root);
    R.stats = qs('[data-ccw-stats]', R.root);
    R.statItems = qs('[data-ccw-stat-items]', R.root);
    R.statValue = qs('[data-ccw-stat-value]', R.root);
    R.statValueLabel = qs('[data-ccw-stat-value-label]', R.root);
    R.lines = qs('[data-ccw-lines]', R.root);
    R.dispatch = qs('[data-ccw-dispatch]', R.root);
    R.accountBody = qs('[data-ccw-account-body]', R.root);
    return true;
  }

  /* Insert a node among ordered siblings without moving any existing node —
     moving a node blurs it in some engines. Siblings that carry no order
     attribute are ignored as anchors (the dispatch line carries a very high
     one so registered lines always land above it). */
  function insertOrdered(parent, node, order, attrName) {
    var kids = Array.prototype.slice.call(parent.children);
    var before = null;
    for (var i = 0; i < kids.length; i++) {
      var o = parseInt(kids[i].getAttribute(attrName), 10);
      if (isNaN(o)) { continue; }
      if (o > order) { before = kids[i]; break; }
    }
    if (before) { parent.insertBefore(node, before); } else { parent.appendChild(node); }
  }

  /* ------------------------------------------- observer + self-mutation mute */
  var mo = null;
  var muting = false;
  var moTimer = null;
  var appRendered = false;

  function withMute(fn) {
    muting = true;
    try { fn(); }
    catch (e) { warn(e); }
    finally {
      if (mo) { try { mo.takeRecords(); } catch (e) { warn(e); } }
      window.setTimeout(function () { muting = false; }, 0);
    }
  }

  /* ------------------------------------------------------------ card model */
  function cards() { return R.grid ? qsa('.iwishItem', R.grid) : []; }

  /* The value posted to the cart. MUTABLE — the size switcher rewrites it. */
  function variantId(card) {
    var i = qs('form input[name="id"]', card);
    return i && i.value ? String(i.value) : '';
  }

  /* The persistent identity. Frozen at first sight, so switching size in the
     UI cannot orphan the card's saved-date or its price history. Do not
     "tidy" this: change it and every card re-enriches on every pass, which
     saturates the fetch pool. */
  function key(card) {
    if (!card.__ccwKey) {
      if (!card.__ccwSavedVid) { card.__ccwSavedVid = variantId(card) || '0'; }
      card.__ccwKey = (attr(card, 'data-pid') || attr(card, 'data-handle') || 'x') + ':' + card.__ccwSavedVid;
    }
    return card.__ccwKey;
  }

  /* The id the app has on file for this card, captured before any switcher can
     rewrite the input. The app's own remove link still points at it. */
  function savedVariantId(card) {
    key(card);
    return card.__ccwSavedVid || '';
  }

  /* The single reliable price source. The price token emits a span carrying
     data-price in cents. If the app's money reformat has stripped it, parse the
     visible text — which is the number the customer is actually looking at. */
  function priceCents(card) {
    var wrap = qs('[data-ccw-price]', card);
    if (!wrap) { return 0; }
    var live = card.__ccwLivePrice;
    if (typeof live === 'number' && live > 0) { return live; }
    var span = qs('[data-price]', wrap) || (wrap.hasAttribute && wrap.hasAttribute('data-price') ? wrap : null);
    if (span) {
      var n = parseInt(span.getAttribute('data-price'), 10);
      if (!isNaN(n) && n > 0) { return n; }
    }
    var m = text(wrap).replace(/[^0-9.,]/g, '').replace(/,/g, '');
    var f = parseFloat(m);
    return isNaN(f) ? 0 : Math.round(f * 100);
  }

  function title(card) { return text(qs('.ccw-card__title', card)); }

  /* Prefers the LIVE selection over the DOM node. The variant line is written
     by the app for the saved variant; once the shopper uses the size switcher
     it is applyVariant that owns the truth. Extensions read this accessor to
     decide badges, filters and masthead counts, so if it lags the selection
     they describe a variant the shopper is no longer buying. */
  function variantTitle(card) {
    if (card && card.__ccwLiveVariant && card.__ccwLiveVariant.title) {
      return prettyVariant(card.__ccwLiveVariant.title);
    }
    return text(qs('.ccw-card__variant', card));
  }

  function isSoldOut(card) {
    var st = card.__ccwState;
    if (st && st.resolved) { return !!st.sold; }
    var f = qs('form', card);
    if (f && f.className && f.className.indexOf('product-soldout') !== -1) { return true; }
    var b = qs('.iwishBuyBtn', card);
    return !!(b && (b.disabled || (b.className || '').indexOf('disabled') !== -1));
  }

  /* DANGEROUS GOODS. Deliberately shared, not store-specific: the "[DG]" title
     prefix and the "-DG" SKU suffix are a cross-store convention for
     lithium-battery road-freight-only stock. Any store that ships a cell needs
     it, and the more of its catalogue ships that way the more it needs it — so
     this is the last thing to move into a store module. */
  function isDG(card) {
    var sku = attr(card, 'data-sku');
    if (/-DG$/i.test(sku)) { return true; }
    return /\[DG\]/i.test(title(card));
  }

  /* ------------------------------- product JSON: throttled AND de-duplicated */
  var CACHE_TTL = 10 * 60 * 1000;
  var inflight = 0;
  var queue = [];
  var PJ = {};   /* handle -> resolved product (or null). In-memory, so it keeps
                    working when sessionStorage throws (Safari private mode). */
  var PW = {};   /* handle -> [callbacks] waiting on an in-flight request */

  function pump() {
    while (inflight < 4 && queue.length) {
      var job = queue.shift();
      inflight++;
      job(function () { inflight--; pump(); });
    }
  }

  /* NOTE the synchronous return paths (memo hit, warm sessionStorage hit).
     `done` can fire INLINE, so anything downstream of a fetchProduct call must
     be correct when it runs in the same tick as the call. This is what made
     the old module-scope boot so dangerous: on a warm cache the whole
     enrichment completed before any extension had parsed. */
  function fetchProduct(handle, done) {
    if (!handle) { done(null); return; }
    if (Object.prototype.hasOwnProperty.call(PJ, handle)) { done(PJ[handle]); return; }
    if (PW[handle]) { PW[handle].push(done); return; }

    var ck = 'ccw:pj:' + handle;
    var hit = store.sget(ck, null);
    if (hit && hit.t && (Date.now() - hit.t) < CACHE_TTL) { PJ[handle] = hit.d; done(hit.d); return; }
    if (!window.fetch) { PJ[handle] = null; done(null); return; }

    PW[handle] = [done];
    queue.push(function (release) {
      window.fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin', headers: { accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function (e) { warn(e); return null; })
        .then(function (j) {
          if (j) { store.sset(ck, { t: Date.now(), d: j }); }
          PJ[handle] = j || null;
          var waiters = PW[handle] || [];
          delete PW[handle];
          waiters.forEach(function (cb) { try { cb(PJ[handle]); } catch (e) { warn(e); } });
          release();
        });
    });
    pump();
  }

  /* ------------------------------------- skeletons + next-visit cache warming
     THE MEASURED PROBLEM. The app issues three of its own server round-trips
     (the proxy page by XHR, twice, plus its fetch_wishlist endpoint) AFTER
     window load, and writes no card until they all return — about 1.9s during
     which the masthead sits over a void. None of that is ours and none of it
     can be cancelled from a theme asset.

     What IS ours: the app's localStorage tells us how many items are coming
     the moment we parse, so we can paint placeholders in the real grid; and
     every enrichment we have ever done tells us which product handle each
     saved id resolves to, so on the NEXT visit we can fetch the product JSON
     during the app's dead time instead of 450ms after it renders.
     ------------------------------------------------------------------------ */

  /* Two rows of the widest grid. A skeleton's only job is "this page is
     alive"; painting 48 throwaway nodes to be wiped 1.9 seconds later is
     churn with no perceptual return. */
  var SKEL_MAX = 12;

  /* FIFO with 4 concurrent (see pump), and prefetch jobs share that queue —
     so a prefetch that is too large queues AHEAD of the real enrichment and
     delays the thing it exists to accelerate. 12 at the measured ~250ms is
     3 rounds ~750ms, starting ~1990ms, finished long before enrichment can
     begin. Redo that arithmetic before raising this. */
  var PREFETCH_MAX = 12;

  /* The app's own store: {productId: [variantId, ...]}. One CARD per saved
     VARIANT, not per product, which is why this sums the arrays rather than
     counting keys. Everything here is defensive — this is another vendor's
     storage format and an app update may reshape it without telling us. A
     shape we do not recognise yields [], and zero skeletons is exactly
     today's behaviour, so there is no failure mode worse than the status quo. */
  function savedIds() {
    var raw = store.get('iWishlistmain', null);
    if (!raw || typeof raw !== 'object' || isArray(raw)) { return []; }
    var ids = [];
    var keys = Object.keys(raw);
    for (var i = 0; i < keys.length; i++) {
      var v = raw[keys[i]];
      var n = isArray(v) ? v.length : 1;
      for (var j = 0; j < n; j++) { ids.push(keys[i]); }
    }
    return ids;
  }

  /* `ids` is the boot-time parse, handed down rather than re-read. Deliberately
     a PARAMETER and not a module-level memo: the app rewrites its store on
     every add and every remove the shopper makes on this page, so a latch here
     would go stale with nothing in this file to invalidate it — and the next
     person to want a count (a repaint on the app's pagination hook, say) would
     get the boot-time list and paint placeholders for items already removed.
     A parameter is alive for exactly one tick, which is exactly as long as the
     value is true for. The fallback keeps the function correct if it is ever
     called from somewhere with no list to hand. */
  function skeletonCount(ids) {
    var n = (ids || savedIds()).length;
    if (!n) {
      /* Fallback: the count key survives even if the map is wiped or
         reshaped. parseInt, not JSON.parse — the app writes it as a bare
         string, which JSON.parse would reject. */
      var c = 0;
      try { c = parseInt(window.localStorage.getItem('iWishCnt'), 10) || 0; }
      catch (e) { c = 0; }
      n = c;
    }
    return Math.max(0, Math.min(n, SKEL_MAX));
  }

  /* NOT a card. No .iwishItem class, no form, no [data-ccw-pick], no
     [data-ccw-price], no data-handle. That is what keeps it out of cards() —
     the single choke point every consumer goes through — and therefore out of
     the item count, the total value, the sort, the filters, the bulk
     selection, the enrichment and the index numerals. */
  function skeletonNode() {
    var d = D.createElement('div');
    d.className = 'ccw-skel';
    d.setAttribute('data-ccw-skel', '');
    d.setAttribute('aria-hidden', 'true');
    /* Belt and braces on top of having no focusable descendants at all: a
       skeleton must never take a tab stop away from the toolbar, which IS
       reachable while these are on screen. */
    try { d.inert = true; } catch (e) { /* older engines simply lack it */ }
    /* Hardcoded literal, no interpolation — nothing from storage or from the
       shopper reaches this string. */
    /* The bar list mirrors the real card slot for slot, INCLUDING a second
       title line and a size row, because that is what the median card actually
       has — measured across a live list, titles run to two lines and most
       products carry a size switcher. An earlier version modelled a one-line
       title and no sizes and came out 197px short, so the grid lurched
       downwards the moment the real cards landed. */
    d.innerHTML =
      '<div class="ccw-skel__top"></div>' +
      '<div class="ccw-skel__media"></div>' +
      '<div class="ccw-skel__bar ccw-skel__badges"></div>' +
      '<div class="ccw-skel__bar ccw-skel__title"></div>' +
      '<div class="ccw-skel__bar ccw-skel__title2"></div>' +
      '<div class="ccw-skel__bar ccw-skel__variant"></div>' +
      '<div class="ccw-skel__bar ccw-skel__price"></div>' +
      '<div class="ccw-skel__bar ccw-skel__meta"></div>' +
      '<div class="ccw-skel__bar ccw-skel__sizes"></div>' +
      '<div class="ccw-skel__slack"></div>' +
      '<div class="ccw-skel__actions"></div>';
    return d;
  }

  var skels = [];

  /* MUST be called before the MutationObserver is created — see boot(). The
     observer does not exist yet when these are inserted, so the injection
     cannot emit a record, cannot be misread as the app's render, and needs no
     withMute(). Move this below the observer and our own paint sets
     appRendered, which lets the empty state through: the exact bug the
     appRendered gate exists to prevent. */
  function paintSkeletons(ids) {
    if (!R.grid || skels.length) { return; }
    if (appRendered) { return; }
    if (R.grid.firstElementChild) { return; }   /* app already wrote cards */
    var n = skeletonCount(ids);
    if (!n) { return; }
    var frag = D.createDocumentFragment();
    for (var i = 0; i < n; i++) {
      var s = skeletonNode();
      skels.push(s);
      frag.appendChild(s);
    }
    R.grid.appendChild(frag);
  }

  /* ONE rule: skeletons live exactly as long as appRendered === false.
     Idempotent, and it must run BEFORE the first pass that follows the app's
     render — the :nth-child entry-animation delays count ALL siblings, so a
     surviving skeleton would give the first real card a stagger delay from
     nowhere, and applySort's fragment append would order real cards after it. */
  function clearSkeletons() {
    if (!skels.length) { return; }
    var list = skels;
    skels = [];
    /* Muted because on the primary path we are called from INSIDE the observer
       callback. An unmuted removal enqueues a fresh childList record, which the
       observer reads as a second app render and which buys us a pass we have
       already scheduled. Not a loop — this is one-shot — but it is exactly the
       class of self-mutation this file mutes everywhere else.
       The parentNode test is not noise: the app REPLACES the grid's innerHTML,
       so on the normal path these nodes are already detached. */
    withMute(function () {
      list.forEach(function (s) { if (s.parentNode) { s.parentNode.removeChild(s); } });
    });
  }

  /* id -> handle, persisted. Versioned and discarded wholesale on a mismatch:
     the map is cheap enough that migration code would cost more than it saves.
     Stores handles only — a stale price in localStorage is the one thing this
     page exists to detect, and a stale title would end up in an attribute,
     which header note 5 forbids. */
  function readIdMap() {
    var r = store.get(K_IDMAP, null);
    if (!r || r.v !== MAP_V || !r.m || typeof r.m !== 'object') { return {}; }
    if (!r.t || (Date.now() - r.t) > MAP_TTL) { return {}; }
    return r.m;
  }

  /* Rewrite-on-merge, using object key insertion order as an LRU proxy: this
     visit's harvest goes in first, so the truncation at MAP_MAX drops the
     coldest entries. That proxy is only true because every key is written
     through MAP_KEY — see the note there before you "simplify" the prefix
     away. ~40 bytes an entry, so ~2.4KB at the cap. store.set already
     swallows QuotaExceededError. */
  function writeIdMap(harvest) {
    var prev = readIdMap();
    var out = {};
    var n = 0;
    Object.keys(harvest).forEach(function (k) {
      if (n < MAP_MAX) { out[MAP_KEY + k] = String(harvest[k]); n++; }
    });
    Object.keys(prev).forEach(function (k) {
      if (n >= MAP_MAX || out[k]) { return; }
      if (typeof prev[k] === 'string' && prev[k]) { out[k] = prev[k]; n++; }
    });
    store.set(K_IDMAP, { v: MAP_V, t: Date.now(), m: out });
  }

  /* Warm the SAME caches enrich() reads, through the SAME fetchProduct, so
     there is no second pool, no second TTL and nothing to keep in sync. By the
     time enrich() runs, a prefetched handle is either memoised (fetchProduct
     returns synchronously, zero requests) or still in flight (the enrichment
     callback joins the existing waiter list) — so it cannot double-fetch.

     There IS a third state and it is the dangerous one: fetchProduct memoises
     FAILURE as well as success (PJ[handle] = j || null), and a memoised null is
     handed back synchronously forever after. Prefetching moves that single
     attempt into the most congested moment of the load — the app's own three
     round-trips are in flight — so a wobble there would leave enrich() with no
     product at all for that card: no size switcher, no live availability, no
     price diff, and enrichSig already latched so nothing retries. prefetch()
     below is what stops a miss latching.

     The callback does nothing on success. We want the side effect — PJ and
     sessionStorage warm — and nothing else. NEVER renderSizes() from here: a
     switcher built before the store's variant filters have registered is the
     warm-cache bug that used to latch an unfiltered switcher for the whole
     page view.

     First visit gets nothing from this: there is no map yet, this visit writes
     one. That is the design, not an oversight — there is no reliable storefront
     endpoint that turns a product id into a handle. */
  /* A named helper, not an inline callback, for a boring reason: `var h` in the
     loop below is ONE binding shared by every iteration, so an inline callback
     would delete the last handle's memo instead of its own.
     Deleting on a miss strands nothing — by the time this runs the request has
     resolved and PW[handle] has been flushed to its waiters — it simply hands
     the retry back to enrich(), at the quiet moment it always used to run.
     Worst case on a genuinely dead handle is one duplicate 404. */
  function prefetch(handle) {
    fetchProduct(handle, function (p) {
      if (!p) { try { delete PJ[handle]; } catch (e) { warn(e); } }
    });
  }

  function warmCache(ids) {
    if (!window.fetch) { return; }
    ids = ids || savedIds();
    if (!ids.length) { return; }
    var map = readIdMap();
    var seen = {};
    var n = 0;
    for (var i = 0; i < ids.length && n < PREFETCH_MAX; i++) {
      /* hasOwnProperty, not a bare bracket read: the KEYS here come from
         another vendor's storage, so an id of 'constructor' or 'toString' would
         otherwise resolve to an inherited Object.prototype member — truthy, and
         stringified straight into a request URL that burns one of the four
         concurrency slots during the paint-critical window. The typeof test
         catches the same class of junk arriving as a VALUE, which readIdMap
         validates the envelope of but not the contents. */
      var mk = MAP_KEY + ids[i];
      var h = Object.prototype.hasOwnProperty.call(map, mk) ? map[mk] : null;
      /* Unknown id, or a handle we have already queued this pass. */
      if (typeof h !== 'string' || !h || seen[h]) { continue; }
      seen[h] = true;
      n++;
      prefetch(h);
    }
  }

  /* ------------------------------------------------------------- rendering */
  function badge(label, cls) {
    var s = D.createElement('span');
    s.className = 'ccw-badge' + (cls ? ' ' + cls : '');
    s.textContent = label;
    return s;
  }

  function toneClass(tone) {
    if (tone === 'accent') { return 'ccw-badge--accent'; }
    if (tone === 'out') { return 'ccw-badge--out'; }
    return '';
  }

  function renderBadges(card) {
    var slot = qs('[data-ccw-badges]', card);
    if (!slot) { return; }
    slot.textContent = '';
    var out = [];
    var st = card.__ccwState || {};

    if (isSoldOut(card)) { out.push(badge('Sold out', 'ccw-badge--out')); }
    else if (st.backInStock) { out.push(badge('Back in stock', 'ccw-badge--accent')); }

    if (st.dropCents > 0) { out.push(badge('Dropped ' + money(st.dropCents), 'ccw-badge--accent')); }
    else if (st.onSale) { out.push(badge('On sale', 'ccw-badge--accent')); }

    if (isDG(card)) { out.push(badge('Road freight only')); }

    /* Extension badges land LAST, so under the hard cap of three they are the
       first to be dropped. The product argument is null on every pre-network
       pass — extensions must tolerate that. */
    if (EXT.badges.length) {
      var product = PJ[attr(card, 'data-handle')] || null;
      EXT.badges.forEach(function (spec) {
        var extra = safe(spec.fn, [card, product, st], null);
        if (!extra || !extra.length) { return; }
        extra.forEach(function (d) {
          if (d && d.label) { out.push(badge(String(d.label), toneClass(d.tone))); }
        });
      });
    }

    out.slice(0, 3).forEach(function (b) { slot.appendChild(b); });
  }

  function renderMeta(card) {
    var slot = qs('[data-ccw-meta]', card);
    if (!slot) { return; }
    var seen = store.get(K_SEEN, {}) || {};
    var k = key(card);
    if (!seen[k]) { seen[k] = Date.now(); store.set(K_SEEN, seen); }
    var days = Math.floor((Date.now() - seen[k]) / 86400000);
    /* Suppressed under two days — otherwise every item reads "Saved today" on
       first run, which looks broken rather than new. */
    if (days < 2) { slot.textContent = ''; return; }
    var s;
    if (days < 14) { s = days + ' days ago'; }
    else if (days < 60) { s = Math.round(days / 7) + ' weeks ago'; }
    else { s = Math.round(days / 30) + ' months ago'; }
    if (slot.textContent !== 'Saved ' + s) { slot.textContent = 'Saved ' + s; }
  }

  /* Switching size must reconcile the WHOLE card state, not just the price.
     The app owns sold-out styling via a class on the form and the disabled
     attribute on the button; if we change the posted variant without moving
     those, the shopper is left with a disabled button on an in-stock size.

     It must also rewrite the VARIANT LINE. That node is the only source for
     util.variantTitle(), which is the documented accessor extensions build
     their badges, filters and counts on — leave it stale and a card can post
     one variant while telling the shopper, and every extension hook, that it
     is another. */
  function applyVariant(card, v) {
    var input = qs('form input[name="id"]', card);
    var form = qs('form', card);
    var btn = qs('.iwishBuyBtn', card);
    var st = card.__ccwState || (card.__ccwState = {});

    if (input) { input.value = String(v.id); }
    card.__ccwLiveVariant = v;
    card.__ccwLivePrice = v.price;
    st.price = v.price;
    st.available = !!v.available;
    st.sold = !v.available;
    st.resolved = true;
    st.onSale = !!(v.compare_at_price && v.compare_at_price > v.price);
    st.compare = v.compare_at_price || 0;

    if (form) {
      form.classList.toggle('product-soldout', !v.available);
    }
    if (btn) {
      btn.disabled = !v.available;
      btn.textContent = v.available ? 'Add to cart' : 'Sold out';
      if (v.available) { btn.removeAttribute('aria-disabled'); } else { btn.setAttribute('aria-disabled', 'true'); }
    }
    var p = qs('[data-ccw-price]', card);
    if (p) { p.textContent = money(v.price); }

    var vt = qs('.ccw-card__variant', card);
    if (vt) {
      var line = prettyVariant(v.title);
      if (line === 'Default Title') { line = ''; }
      if (vt.textContent !== line) { vt.textContent = line; }
    }

    renderBadges(card);
    syncCheckbox(card);
    refreshStats();
  }

  /* Run the registered variant filters. CONTRACT (see the header): a filter
     receives the full variant array and the product JSON and returns the
     variants to SHOW. It may only REMOVE or REORDER — anything it returns that
     is not in product.variants is discarded here, because posting an id that
     does not exist on the product is a 404 at the cart.

     The saved variant id is deliberately NOT passed. There is no "the saved
     variant is exempt" rule: a saved variant the store wants hidden is exactly
     the case these filters exist for. Reinstating it here would make the hook
     unable to do the one job it has. */
  function visibleVariants(product, card) {
    var all = (product && product.variants) || [];
    var byId = {};
    all.forEach(function (v) { byId[String(v.id)] = v; });

    var list = all.slice();
    EXT.variants.forEach(function (spec) {
      var out = safe(spec.fn, [list.slice(), product, card], null);
      if (isArray(out)) { list = out; }
    });

    var seenIds = {};
    return list.filter(function (v) {
      var id = v && String(v.id);
      if (!id || !byId[id] || seenIds[id]) { return false; }
      seenIds[id] = true;
      return true;
    });
  }

  function renderSizes(card, product) {
    var slot = qs('[data-ccw-sizes]', card);
    if (!slot || !product || !product.variants || product.variants.length < 2) { return; }

    /* The latch is stamped with the SIZE OF THE EXTENSION SET it was built
       under, not a bare boolean. A plain boolean cannot tell "already built
       correctly" from "built before the store's filters existed", and the
       second one is permanent — which is how a warm cache used to leave
       filtered-out variants on screen for the whole page view. */
    var stamp = EXT.variants.length;
    if (slot.__ccwBuilt && slot.__ccwBuiltWith === stamp) { return; }

    var vid = variantId(card);
    var variants = visibleVariants(product, card);

    /* SAFETY NET. A filter that hides everything must never produce an empty
       or missing switcher, and must never break add to cart. Fall back to the
       currently-selected variant alone: one inert button that states what the
       cart will post, which is honest, rather than a void. */
    var fallback = false;
    if (!variants.length) {
      var current = null;
      for (var f = 0; f < product.variants.length; f++) {
        if (String(product.variants[f].id) === vid) { current = product.variants[f]; break; }
      }
      if (!current) { return; }
      variants = [current];
      fallback = true;
      warn('all variants filtered out — falling back to the selected variant');
    }

    /* The saved variant is gone from the switcher. Do NOT quietly re-add it:
       the store asked for it to be hidden. Move the card onto the first
       variant that survives — preferring one that can actually be bought —
       and say so, because the app's stored record and its remove link still
       point at the original. */
    var hasSaved = false;
    for (var s = 0; s < variants.length; s++) {
      if (String(variants[s].id) === vid) { hasSaved = true; break; }
    }
    var replaced = false;
    if (!hasSaved && !fallback) {
      var pick = null;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].available) { pick = variants[i]; break; }
      }
      if (!pick) { pick = variants[0]; }
      applyVariant(card, pick);
      vid = String(pick.id);
      replaced = true;
    }

    /* Filtering can collapse a multi-variant product to a single real choice.
       A lone size button is a control that cannot do anything — unless it is
       the safety fallback or a forced replacement, where it is the only way to
       tell the shopper what the cart is going to post. Bail otherwise, and do
       NOT latch, so a later pass with better data can still build it.
       (Latching before this bail permanently poisons the slot.) */
    if (variants.length < 2 && !fallback && !replaced) { return; }

    slot.__ccwBuilt = true;
    slot.__ccwBuiltWith = stamp;
    slot.textContent = '';

    /* The note is built now but stays hidden until the shopper actually
       changes a size — rendering it up front repeated the same sentence under
       every multi-variant card, a wall of microcopy answering a question
       nobody had asked. The exception is a forced replacement: there the
       shopper has not touched anything and still needs to be told. */
    var note = D.createElement('p');
    note.className = 'ccw-sizes__note';
    note.textContent = (replaced || fallback) ? COPY.sizesUnavailable : COPY.sizesNote;
    note.hidden = !(replaced || fallback);

    variants.slice(0, 4).forEach(function (v) {
      var b = D.createElement('button');
      b.type = 'button';
      b.className = 'ccw-size';
      b.textContent = prettyVariant(v.title);
      b.setAttribute('aria-pressed', String(v.id) === vid ? 'true' : 'false');
      if (!v.available) { b.disabled = true; b.title = 'Sold out'; }
      on(b, 'click', guard(function () {
        qsa('.ccw-size', slot).forEach(function (o) { o.setAttribute('aria-pressed', 'false'); });
        b.setAttribute('aria-pressed', 'true');
        note.textContent = COPY.sizesNote;
        note.hidden = false;
        applyVariant(card, v);
      }));
      slot.appendChild(b);
    });
    slot.appendChild(note);
    slot.hidden = false;
  }

  /* --------------------------------------------------- availability + prices */
  var enrichSig = '';

  function enrich() {
    var list = cards();
    if (!list.length) { return; }

    /* One enrichment per distinct set of cards. Without this, five boot entry
       points would each queue a full round of product fetches. Cleared by
       invalidate() so a late extension registration can force a re-run. */
    var sig = list.map(key).join('|');
    if (sig === enrichSig) { return; }
    enrichSig = sig;

    var snapshot = store.get(K_SNAP, {}) || {};
    var pending = list.length;
    var changes = { back: [], drops: [] };

    list.forEach(function (card) {
      var handle = attr(card, 'data-handle');
      var st = card.__ccwState || (card.__ccwState = {});
      var prev = snapshot[key(card)];

      /* Server-side truth we already have, before any network call. */
      if (!st.resolved) { st.sold = isSoldOut(card); }
      st.price = priceCents(card);
      var cmp = parseFloat(attr(card, 'data-compare'));
      if (!isNaN(cmp) && cmp > 0) {
        /* The compare token's units are unverified, so this is a magnitude
           heuristic used ONLY until the live JSON lands and overwrites it. */
        var cmpCents = cmp < 1000 ? Math.round(cmp * 100) : Math.round(cmp);
        if (st.price && cmpCents > st.price) { st.onSale = true; st.compare = cmpCents; }
      }
      renderBadges(card);
      renderMeta(card);

      if (!handle) { pending--; if (pending <= 0) { afterEnrich(changes, snapshot); } return; }

      fetchProduct(handle, guard(function (product) {
        if (product && product.variants) {
          var vid = variantId(card);
          var v = null;
          for (var i = 0; i < product.variants.length; i++) {
            if (String(product.variants[i].id) === vid) { v = product.variants[i]; break; }
          }
          if (v) {
            st.available = !!v.available;
            st.sold = !v.available;
            st.price = v.price || st.price;
            card.__ccwLivePrice = v.price;
            st.onSale = !!(v.compare_at_price && v.compare_at_price > v.price);
            if (st.onSale) { st.compare = v.compare_at_price; }

            if (prev) {
              if (prev.available === false && st.available === true) { st.backInStock = true; changes.back.push(card); }
              if (typeof prev.price === 'number' && st.price && prev.price > st.price) {
                st.dropCents = prev.price - st.price;
                changes.drops.push(card);
              }
            }
            st.resolved = true;
          }
          /* Built whether or not the saved variant resolved: a saved variant
             that no longer exists on the product is precisely the card that
             needs a switcher offering something that does. */
          renderSizes(card, product);
          renderBadges(card);
          syncCheckbox(card);
          refreshStats();
        }
        pending--;
        if (pending <= 0) { afterEnrich(changes, snapshot); }
      }));
    });
  }

  function afterEnrich(changes, snapshot) {
    lastChanges = changes;
    guard(renderDispatch)(changes);
    guard(refreshStats)();
    /* Registered lines are re-rendered here too, so anything that depends on
       live availability updates once the JSON has landed. */
    guard(renderMastheadLines)();
    guard(buildChips)();
    /* Filters must be re-applied against the newly resolved state, or a chip
       that has just been removed leaves the grid filtered with no way back. */
    guard(function () { setFilter(activeFilter); })();
    guard(applySort)();

    guard(function () {
      var next = {};
      /* Harvested inside the snapshot walk we are already doing — this is the
         one code path that holds both halves of the id/handle pair, so it
         costs no extra traversal and no extra timing.
         Only handles that ACTUALLY RESOLVED are recorded (PJ[h] truthy).
         Recording straight from the attribute would faithfully persist a
         handle that 404s and we would re-request it every visit, forever. */
      var harvest = {};
      cards().forEach(function (card) {
        var st = card.__ccwState || {};
        next[key(card)] = { price: st.price || 0, available: st.available !== false };
        var pid = attr(card, 'data-pid');
        var h = attr(card, 'data-handle');
        if (pid && h && PJ[h]) { harvest[pid] = h; }
      });
      /* ONE localStorage write for the whole list: store.set serialises
         synchronously, and a write per card is a forced serialisation per card
         for no reason. */
      if (Object.keys(harvest).length) { writeIdMap(harvest); }
      R.root.__ccwPendingSnapshot = next;

      if (!visitRecorded) { visitRecorded = true; store.set(K_VISIT, Date.now()); }
      if (!Object.keys(snapshot || {}).length) {
        /* First ever visit: no news is possible, so seal the snapshot now. */
        store.set(K_SNAP, next);
      }
    })();
  }

  function commitSnapshot() {
    if (R.root && R.root.__ccwPendingSnapshot) { store.set(K_SNAP, R.root.__ccwPendingSnapshot); }
    cards().forEach(function (c) {
      c.classList.remove('is-ccw-unread');
      if (c.__ccwState) { c.__ccwState.dropCents = 0; c.__ccwState.backInStock = false; }
      renderBadges(c);
    });
    if (R.dispatch) { R.dispatch.hidden = true; R.dispatch.textContent = ''; }
    /* Dropped so a late extension registration cannot resurrect a line the
       shopper has explicitly dismissed. */
    lastChanges = null;
    dispatchCommitted = true;
    buildChips();
    setFilter(activeFilter);
    applySort();
  }

  /* ------------------------------------------------------- the dispatch line */
  var lastChanges = null;
  var dispatchCommitted = false;

  function renderDispatch(changes) {
    if (!R.dispatch || !changes) { return; }
    if (dispatchCommitted) { return; }
    /* Idempotent, and it NEVER clears on the early-return path — a later pass
       must not be able to blank a line an earlier pass legitimately rendered.
       invalidate() lifts this flag deliberately, and only then. */
    if (R.dispatch.__ccwRendered) { return; }
    /* Never on a first-ever load, and never twice inside six hours — absence is
       the signal that nothing happened. We do not print "No updates". */
    if (!LAST_VISIT || (Date.now() - LAST_VISIT) < 6 * 3600 * 1000) { return; }

    var clauses = [];
    if (changes.back.length) {
      clauses.push({
        n: changes.back.length,
        word: changes.back.length === 1 ? COPY.backOne : COPY.backMany,
        filter: 'back'
      });
    }
    if (changes.drops.length) {
      var best = changes.drops[0];
      changes.drops.forEach(function (c) { if ((c.__ccwState.dropCents || 0) > (best.__ccwState.dropCents || 0)) { best = c; } });
      clauses.push({ n: changes.drops.length, word: 'price drop', filter: 'drop', label: title(best) + ' dropped ' + money(best.__ccwState.dropCents) });
    }
    if (!clauses.length) { return; }

    R.dispatch.textContent = '';
    var frag = D.createDocumentFragment();
    frag.appendChild(D.createTextNode('Since you were last here — '));
    clauses.slice(0, 2).forEach(function (c, i) {
      if (i) { frag.appendChild(D.createTextNode(' and ')); }
      var b = D.createElement('button');
      b.type = 'button';
      b.className = 'ccw-dispatch__clause';
      b.textContent = c.label ? c.label : (c.n + ' ' + c.word);
      on(b, 'click', guard(function () { setFilter(c.filter); }));
      frag.appendChild(b);
    });
    frag.appendChild(D.createTextNode('.'));

    var clear = D.createElement('button');
    clear.type = 'button';
    clear.className = 'ccw-dispatch__clear';
    clear.textContent = 'Clear';
    on(clear, 'click', guard(commitSnapshot));
    frag.appendChild(clear);

    R.dispatch.appendChild(frag);
    R.dispatch.hidden = false;
    R.dispatch.__ccwRendered = true;

    changes.back.concat(changes.drops).forEach(function (c) { c.classList.add('is-ccw-unread'); });
  }

  /* -------------------------------------------------- registered masthead lines */
  function renderMastheadLines() {
    if (!R.lines || !EXT.lines.length) { return; }
    var ctx = { cards: cards(), money: money };
    EXT.lines.forEach(function (spec) {
      var out = safe(spec.render, [ctx], null);
      var node = spec.__node;
      if (!node) {
        /* Nothing to say yet and no node built: build nothing. An empty line
           element would leave a stray gap on a page that never fills it. */
        if (!out) { return; }
        node = D.createElement('p');
        node.className = 'ccw-line';
        node.setAttribute('data-ccw-line', spec.id);
        node.setAttribute('data-ccw-line-order', String(spec.order));
        spec.__node = node;
        insertOrdered(R.lines, node, spec.order, 'data-ccw-line-order');
      }
      if (!out) { node.hidden = true; return; }
      out = String(out);
      /* Write only on change: replacing the text on every pass would restart
         any transition and re-announce inside a live region. */
      if (node.textContent !== out) { node.textContent = out; }
      node.hidden = false;
    });
  }

  /* --------------------------------------------------------- registered copy */
  /* Writes only the keys a store has actually overridden. An un-overridden key
     leaves the markup exactly as the template author wrote it, which is what
     lets the template ship store-neutral defaults and still be portable. */
  function renderCopy() {
    if (!R.root) { return; }
    Object.keys(COPY_NODES).forEach(function (k) {
      if (!COPY_SET[k]) { return; }
      var node = qs(COPY_NODES[k], R.root);
      if (!node) { return; }
      var s = String(COPY[k]);
      if (node.textContent !== s) { node.textContent = s; }
    });
  }

  /* ------------------------------------------------------------------ stats */
  function selected() {
    return cards().filter(function (c) { var i = qs('[data-ccw-pick]', c); return i && i.checked; });
  }

  function renderStatCells(ctx) {
    if (!R.stats || !EXT.stats.length) { return; }
    EXT.stats.forEach(function (spec) {
      var out = safe(spec.value, [ctx], null);
      var node = spec.__node;
      if (!node) {
        if (out === null || out === undefined || out === '') { return; }
        node = D.createElement('div');
        node.className = 'ccw-stat';
        node.setAttribute('data-ccw-stat-ext', spec.id);
        node.setAttribute('data-ccw-stat-order', String(spec.order));
        var lab = D.createElement('span');
        lab.className = 'ccw-stat__label';
        lab.textContent = spec.label || '';
        var v = D.createElement('span');
        v.className = 'ccw-stat__value';
        node.appendChild(lab);
        node.appendChild(v);
        spec.__node = node;
        spec.__val = v;
        insertOrdered(R.stats, node, spec.order, 'data-ccw-stat-order');
      }
      if (out === null || out === undefined || out === '') { node.hidden = true; return; }
      out = String(out);
      if (spec.__val.textContent !== out) { spec.__val.textContent = out; }
      node.hidden = false;
    });
  }

  function refreshStats() {
    if (!R.root) { return; }
    var list = cards();
    var sel = selected();
    var basis = sel.length ? sel : list;
    var total = 0;
    basis.forEach(function (c) { total += priceCents(c); });

    if (R.stats) { R.stats.hidden = list.length === 0; }
    if (R.statItems) { R.statItems.textContent = String(list.length); }
    if (R.statValue) { R.statValue.textContent = money(total); }
    if (R.statValueLabel) { R.statValueLabel.textContent = sel.length ? 'Selected' : 'Value'; }

    /* The latch is per-node and is CLEARED by setCopy, so a store that
       registers its wording after the first pass still gets it on screen. The
       gate is the item count alone: an unresolved total drops the value clause
       (see fillCopy) rather than suppressing the whole sentence, because a
       suppressed sentence used to latch nothing and silently discard the
       override on an empty or slow-loading list. */
    if (R.accountBody && list.length && !R.accountBody.__ccwDone) {
      R.accountBody.textContent = fillCopy(COPY.accountBody, {
        n: list.length + (list.length === 1 ? ' item' : ' items'),
        v: total > 0 ? money(total) : ''
      });
      R.accountBody.__ccwDone = true;
    }

    renderStatCells({ cards: list, selected: sel, totalCents: total, money: money });
    renderBulk(sel);
  }

  function renderBulk(sel) {
    if (!R.bulk || !R.bulkSummary) { return; }
    if (!sel.length) {
      R.bulk.hidden = true;
      R.root.style.paddingBottom = '';
      R.root.style.setProperty('--ccw-bulk-h', '0px');
      return;
    }
    var total = 0;
    sel.forEach(function (c) { total += priceCents(c); });
    var hidden = sel.filter(function (c) { return c.style.display === 'none'; }).length;
    var addable = sel.filter(function (c) { return !isSoldOut(c); }).length;

    R.bulkSummary.textContent = '';
    var b = D.createElement('b');
    b.textContent = sel.length + ' selected';
    R.bulkSummary.appendChild(b);
    R.bulkSummary.appendChild(D.createTextNode(' · ' + money(total)));
    if (hidden) {
      var note = D.createElement('span');
      note.className = 'ccw-bulk__note';
      note.textContent = hidden + ' of these ' + (hidden === 1 ? 'is' : 'are') + ' hidden by the current filter.';
      R.bulkSummary.appendChild(note);
    }
    var btn = qs('[data-ccw-addselected]', R.bulk);
    if (btn) { btn.textContent = 'Add ' + addable + ' to cart'; btn.disabled = addable === 0; }
    R.bulk.hidden = false;

    /* Measure it, so the toast can sit above it and the page can reserve room
       under it — otherwise the bar covers the last row and the pagination. */
    var h = R.bulk.offsetHeight || 0;
    R.root.style.setProperty('--ccw-bulk-h', h + 'px');
    R.root.style.paddingBottom = (h + 32) + 'px';
  }

  /* Returns true when it actually changed the checked state, so the caller
     knows the selection totals need recomputing. */
  function syncCheckbox(card) {
    var i = qs('[data-ccw-pick]', card);
    if (!i) { return false; }
    var changed = false;
    var t = title(card);
    if (isSoldOut(card)) {
      /* The bulk-add 422 guard, made visible. A silently dead checkbox just
         leaves the shopper wondering why the count will not go up. */
      if (i.checked) { i.checked = false; changed = true; }
      i.disabled = true;
      i.setAttribute('title', 'Sold out — cannot be added');
      i.setAttribute('aria-label', (t ? t + ' — ' : '') + 'sold out, cannot be added');
    } else {
      i.disabled = false;
      i.removeAttribute('title');
      if (t) { i.setAttribute('aria-label', 'Select ' + t); }
    }
    return changed;
  }

  /* --------------------------------------------------------- filter and sort */
  /* "order" exists so registered filters can be spliced into a stable
     position. buildChips walks this array BY INDEX to find its insert anchor;
     if the order changed between passes, chips would be moved, and moving a
     node blurs it mid-interaction for keyboard users. */
  var FILTERS = [
    { id: 'all', label: 'All', order: 0, test: function () { return true; } },
    { id: 'stock', label: 'In stock', order: 30, test: function (c) { return !isSoldOut(c); } },
    { id: 'drop', label: 'Price drops', order: 40, test: function (c) { return ((c.__ccwState || {}).dropCents || 0) > 0; } },
    { id: 'sale', label: 'On sale', order: 50, test: function (c) { return !!(c.__ccwState || {}).onSale; } },
    { id: 'back', label: 'Back in stock', order: 60, test: function (c) { return !!(c.__ccwState || {}).backInStock; } }
  ];
  var activeFilter = 'all';

  function filterById(id) {
    for (var i = 0; i < FILTERS.length; i++) { if (FILTERS[i].id === id) { return FILTERS[i]; } }
    return null;
  }

  /* Diffed, not rebuilt. Wiping and recreating the rail would throw focus to
     document.body every time enrichment resolved — which is exactly when a
     shopper is starting to use it. */
  function buildChips() {
    if (!R.chips) { return; }
    var list = cards();
    var existing = {};
    qsa('.ccw-chip', R.chips).forEach(function (b) { existing[b.getAttribute('data-filter')] = b; });

    FILTERS.forEach(function (f, idx) {
      var n = list.filter(f.test).length;
      var b = existing[f.id];

      if (!n && f.id !== 'all') {
        if (b && b !== D.activeElement && b.parentNode) { b.parentNode.removeChild(b); delete existing[f.id]; }
        return;
      }

      if (!b) {
        b = D.createElement('button');
        b.type = 'button';
        b.className = 'ccw-chip';
        b.setAttribute('data-filter', f.id);
        var lab = D.createElement('span');
        lab.className = 'ccw-chip__l';
        lab.textContent = f.label;
        var c = D.createElement('span');
        c.className = 'ccw-chip__n';
        b.appendChild(lab);
        b.appendChild(c);
        on(b, 'click', guard(function () { setFilter(f.id); }));

        /* Insert at the right place without moving any existing node. */
        var before = null;
        for (var j = idx + 1; j < FILTERS.length; j++) {
          if (existing[FILTERS[j].id]) { before = existing[FILTERS[j].id]; break; }
        }
        if (before) { R.chips.insertBefore(b, before); } else { R.chips.appendChild(b); }
        existing[f.id] = b;
      }

      var nEl = qs('.ccw-chip__n', b);
      if (nEl && nEl.textContent !== String(n)) { nEl.textContent = String(n); }
      var want = activeFilter === f.id ? 'true' : 'false';
      if (b.getAttribute('aria-pressed') !== want) { b.setAttribute('aria-pressed', want); }
    });

    if (R.toolbar) { R.toolbar.hidden = list.length === 0; }
    if (activeFilter !== 'all' && !qs('.ccw-chip[data-filter="' + activeFilter + '"]', R.chips)) {
      setFilter('all');
    }
  }

  function setFilter(id) {
    var f = filterById(id);
    if (!f) { f = FILTERS[0]; }
    activeFilter = f.id;

    var shown = 0;
    cards().forEach(function (c) {
      var ok = f.test(c);
      c.style.display = ok ? '' : 'none';
      if (ok) { shown++; }
    });

    /* Nothing left under this filter: fall back rather than stranding the
       shopper in front of an empty grid. */
    if (!shown && f.id !== 'all' && cards().length) {
      activeFilter = 'all';
      f = FILTERS[0];
      shown = cards().length;
      cards().forEach(function (c) { c.style.display = ''; });
    }

    qsa('.ccw-chip', R.chips).forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-filter') === activeFilter ? 'true' : 'false');
    });

    if (R.visible) {
      var msg = shown + (shown === 1 ? ' item shown' : ' items shown');
      /* Only touch the node when the string actually changes: replacing the
         text re-triggers the live region and re-announces on every boot pass. */
      if (R.visible.textContent !== msg) { R.visible.textContent = msg; }
    }
    renderBulk(selected());
  }

  function applySort() {
    if (!R.grid) { return; }
    var mode = (R.sort && R.sort.value) || store.get(K_SORT, 'recent') || 'recent';
    var seen = store.get(K_SEEN, {}) || {};
    var list = cards();
    if (!list.length) { return; }

    list.sort(function (a, b) {
      /* Unread items are hoisted above everything in the default sort until
         they are cleared. That is what turns a bookmark folder into an inbox. */
      if (mode === 'recent') {
        var ua = a.classList.contains('is-ccw-unread') ? 1 : 0;
        var ub = b.classList.contains('is-ccw-unread') ? 1 : 0;
        if (ua !== ub) { return ub - ua; }
        return (seen[key(b)] || 0) - (seen[key(a)] || 0);
      }
      if (mode === 'plh') { return priceCents(a) - priceCents(b); }
      if (mode === 'phl') { return priceCents(b) - priceCents(a); }
      if (mode === 'az') { return title(a).localeCompare(title(b)); }
      if (mode === 'stock') { return (isSoldOut(a) ? 1 : 0) - (isSoldOut(b) ? 1 : 0); }
      if (mode === 'drop') { return ((b.__ccwState || {}).dropCents || 0) - ((a.__ccwState || {}).dropCents || 0); }
      return 0;
    });

    /* If the DOM is already in this order, touch nothing. Re-appending nodes
       would fire the observer and restart every card's entry animation. */
    var current = Array.prototype.filter.call(R.grid.children, function (n) {
      return n.classList && n.classList.contains('iwishItem');
    });
    var same = current.length === list.length;
    if (same) {
      for (var i = 0; i < list.length; i++) { if (current[i] !== list[i]) { same = false; break; } }
    }
    if (same) { return; }

    withMute(function () {
      var frag = D.createDocumentFragment();
      list.forEach(function (c) { frag.appendChild(c); });
      R.grid.appendChild(frag);
    });
  }

  /* ------------------------------------------------------------ empty state */
  function renderEmptyLinks() {
    /* Its own flag, deliberately NOT the recommendations flag: tying link
       injection to a network response means a failed fetch takes the links
       down with it. Cleared by invalidate(), and the container is emptied
       before the rebuild, so a late registration adds rows rather than
       duplicating the ones already there. */
    if (!R.links || R.links.__ccwLinksDone) { return; }
    R.links.__ccwLinksDone = true;
    R.links.textContent = '';
    EXT.emptyLinks.slice().sort(function (a, b) { return a.order - b.order; }).forEach(function (spec) {
      var a = D.createElement('a');
      a.className = 'ccw-link';
      a.href = spec.href;
      var s = D.createElement('span');
      s.textContent = spec.label;
      var arrow = D.createElement('span');
      arrow.className = 'ccw-link__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';
      a.appendChild(s);
      a.appendChild(arrow);
      R.links.appendChild(a);
    });
  }

  function renderEmpty() {
    /* An empty grid before the app has rendered means "not loaded yet", not
       "empty wishlist". Showing the empty state here reads as "the site lost
       my list" to every returning customer. */
    if (!appRendered) {
      if (R.empty) { R.empty.hidden = true; }
      return;
    }
    var has = cards().length > 0;
    if (R.empty) { R.empty.hidden = has; }
    if (R.toolbar) { R.toolbar.hidden = !has; }

    /* Our empty state is live, so the app's own fallback message (which is
       deliberately NOT hidden in CSS) can now be stood down. */
    qsa('.iwishMsgInfo', R.root).forEach(function (el) { el.style.display = has ? '' : 'none'; });

    if (has) { return; }
    renderEmptyLinks();

    if (!R.recs || R.recs.__ccwDone) { return; }
    R.recs.__ccwDone = true;

    var viewed = store.get('viewedProducts', []) || [];
    if (!viewed.length || !window.fetch) { return; }
    var pid = viewed[0];
    /* FOUR, in both places, and the stylesheet must not offer a fifth column
       for this grid — the count is a constant here, not a data length, so a
       5-up recommendations row can only ever render as four cards and a hole.
       The wishlist grid is the one that legitimately goes wider. */
    window.fetch('/recommendations/products.json?product_id=' + encodeURIComponent(pid) + '&intent=related&limit=4', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(guard(function (j) {
        if (!j || !j.products || !j.products.length || !R.recsGrid) { return; }
        j.products.slice(0, 4).forEach(function (p) {
          var a = D.createElement('a');
          a.className = 'ccw-rec';
          a.href = p.url || ('/products/' + p.handle);
          if (p.featured_image) {
            var img = D.createElement('img');
            img.src = p.featured_image;
            img.alt = '';
            img.loading = 'lazy';
            a.appendChild(img);
          }
          var t = D.createElement('p'); t.className = 'ccw-rec__t'; t.textContent = p.title || '';
          var pr = D.createElement('p'); pr.className = 'ccw-rec__p'; pr.textContent = money(p.price || 0);
          a.appendChild(t); a.appendChild(pr);
          R.recsGrid.appendChild(a);
        });
        R.recs.hidden = false;
      }))
      .catch(warn);
  }

  /* ------------------------------------------------------------------ toast */
  var toastTimer = null;
  function hideToast() {
    if (toastTimer) { window.clearTimeout(toastTimer); toastTimer = null; }
    if (R.toast) { R.toast.hidden = true; }
  }
  function armToast(ms) {
    if (toastTimer) { window.clearTimeout(toastTimer); }
    toastTimer = window.setTimeout(hideToast, ms || 8000);
  }
  function toast(msg, href, hrefLabel) {
    if (!R.toast || !R.toastMsg) { return; }
    /* Unhide FIRST, then write: several screen readers will not announce
       content inserted into a region while it is still display:none. */
    R.toast.hidden = false;
    R.toastMsg.textContent = msg;
    if (href) {
      R.toastMsg.appendChild(D.createTextNode(' '));
      var a = D.createElement('a');
      a.href = href;
      a.textContent = hrefLabel || 'View cart';
      R.toastMsg.appendChild(a);
    }
    armToast(8000);
  }

  function pingCart() {
    guard(function () {
      D.dispatchEvent(new window.CustomEvent('cart:update', { bubbles: true }));
      if (!window.fetch) { return; }
      window.fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) {
          if (!c) { return; }
          qsa('[data-cart-count], .cart-count, .header-actions__cart-count').forEach(function (el) {
            el.textContent = String(c.item_count);
          });
        })
        .catch(warn);
    })();
  }

  /* --------------------------------------------------------- add to cart */
  function bindAddToCart() {
    if (R.root.__ccwCartBound) { return; }
    R.root.__ccwCartBound = true;

    /* The app's add-to-cart is a native full-page POST. We intercept and use
       the AJAX endpoint. CRITICAL: we only fall back to the native POST when
       the request never reached the server. A 422 (variant sold out between
       render and click) is reported in place; a 2xx whose body will not parse
       is treated as SUCCESS. Resubmitting either would double-add or dump the
       shopper on Shopify's raw cart error page. */
    on(R.root, 'submit', function (e) {
      var form = e.target;
      if (!form || !form.classList || form.classList.contains('ccw-native')) { return; }
      if (!window.fetch || !window.FormData) { return; }
      e.preventDefault();
      var card = form.closest ? form.closest('.iwishItem') : null;
      var data = new window.FormData(form);

      window.fetch('/cart/add.js', { method: 'POST', body: data, credentials: 'same-origin', headers: { accept: 'application/json' } })
        .then(function (r) {
          if (r.ok) {
            toast((card ? title(card) : 'Added') + ' — added to your cart.', '/cart', 'View cart →');
            pingCart();
            return null;
          }
          return r.json().catch(function () { return null; }).then(function (j) {
            toast((j && (j.description || j.message)) || 'That item could not be added right now.');
          });
        })
        .catch(guard(function () {
          /* The request itself failed — no response at all. Only here is a
             native resubmit correct. */
          form.classList.add('ccw-native');
          form.submit();
        }));
    });
  }

  function addSelected() {
    var sel = selected().filter(function (c) { return !isSoldOut(c); });
    if (!sel.length || !window.fetch) { return; }
    var items = sel.map(function (c) { return { id: variantId(c), quantity: 1 }; })
      .filter(function (i) { return i.id; });
    if (!items.length) { return; }

    window.fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { if (!r.ok) { throw new Error('bulk failed'); } return true; })
      .then(function () {
        toast(items.length + (items.length === 1 ? ' item' : ' items') + ' added to your cart.', '/cart', 'View cart →');
        pingCart();
        clearSelection();
      })
      .catch(guard(function () {
        /* The bulk endpoint is all-or-nothing: one bad line 422s the batch and
           the shopper gets nothing. Retry one at a time and name what failed. */
        var failed = [];
        var chain = window.Promise.resolve();
        sel.forEach(function (card) {
          chain = chain.then(function () {
            var fd = new window.FormData();
            fd.append('id', variantId(card));
            fd.append('quantity', '1');
            return window.fetch('/cart/add.js', { method: 'POST', body: fd, credentials: 'same-origin' })
              .then(function (r) { if (!r.ok) { failed.push(title(card)); } })
              .catch(function () { failed.push(title(card)); });
          });
        });
        chain.then(function () {
          if (failed.length) {
            toast('Added what we could. ' + failed.join(', ') + ' could not be added right now.', '/cart', 'View cart →');
          } else {
            toast(items.length + ' items added to your cart.', '/cart', 'View cart →');
          }
          pingCart();
          clearSelection();
        });
      }));
  }

  function clearSelection() {
    cards().forEach(function (c) { var i = qs('[data-ccw-pick]', c); if (i) { i.checked = false; } });
    refreshStats();
  }

  /* ------------------------------------------------------ remove confirmation */
  function showConfirm(card, a) {
    var foot = qs('[data-ccw-foot]', card);
    var confirm = qs('[data-ccw-confirm]', card);
    if (!foot || !confirm) { a.__ccwConfirmed = true; a.click(); return; }

    var t = title(card);
    if (t) { confirm.setAttribute('aria-label', 'Confirm removal of ' + t); }

    foot.hidden = true;
    confirm.hidden = false;

    /* The focused element was the remove link, which we just hid — without
       this, focus falls to <body> and a keyboard user loses their place in a
       48-card grid with nothing announced. */
    var yes = qs('[data-ccw-confirm-yes]', confirm);
    var no = qs('[data-ccw-confirm-no]', confirm);
    if (yes && yes.focus) { try { yes.focus(); } catch (e) { warn(e); } }

    function revertNow(refocus) {
      window.clearTimeout(card.__ccwRevert);
      card.__ccwRevert = null;
      foot.hidden = false;
      confirm.hidden = true;
      if (refocus && a && a.focus) { try { a.focus(); } catch (e) { warn(e); } }
    }

    /* Timer lives on the card, not in a closure, so a second Remove click can
       cancel the first one's timer instead of leaving it live. */
    window.clearTimeout(card.__ccwRevert);
    card.__ccwRevert = window.setTimeout(function () {
      /* Never yank the UI out from under someone who is standing in it. */
      if (confirm.contains(D.activeElement)) { return; }
      revertNow(false);
    }, 6000);

    if (!confirm.__ccwBound) {
      confirm.__ccwBound = true;
      if (yes) {
        on(yes, 'click', guard(function () {
          window.clearTimeout(card.__ccwRevert);
          card.classList.remove('is-ccw-new');
          card.classList.add('is-ccw-going');
          window.setTimeout(function () {
            var link = qs('.iwishRemoveBtn', card);
            if (link) { link.__ccwConfirmed = true; link.click(); }
          }, 220);
        }));
      }
      if (no) { on(no, 'click', guard(function () { revertNow(true); })); }
    }
  }

  function bindRemove() {
    if (R.root.__ccwRemoveBound) { return; }
    R.root.__ccwRemoveBound = true;

    /* Capture phase, on our own root, so we outrun the app's element-level
       handler. We never call the app's internals: on confirm we re-dispatch the
       same click with a flag and let the app do its own removal. */
    R.root.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('.iwishRemoveBtn') : null;
      if (!a) { return; }
      if (a.__ccwConfirmed) { a.__ccwConfirmed = false; return; }

      e.preventDefault();
      e.stopImmediatePropagation();

      var card = a.closest('.iwishItem');
      if (!card) { return; }
      guard(showConfirm)(card, a);
    }, true);
  }

  /* ------------------------------------------------------------ misc chrome */
  function bindChrome() {
    if (R.root.__ccwChromeBound) { return; }
    R.root.__ccwChromeBound = true;

    on(R.root, 'change', guard(function (e) {
      if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-ccw-pick')) { refreshStats(); }
    }));

    if (R.sort) {
      var saved = store.get(K_SORT, 'recent');
      if (saved) { R.sort.value = saved; }
      on(R.sort, 'change', guard(function () { store.set(K_SORT, R.sort.value); applySort(); }));
    }

    qsa('[data-ccw-view]', R.root).forEach(function (b) {
      on(b, 'click', guard(function () {
        var v = b.getAttribute('data-ccw-view');
        R.root.setAttribute('data-view', v);
        store.set(K_VIEW, v);
        qsa('[data-ccw-view]', R.root).forEach(function (o) { o.setAttribute('aria-pressed', o === b ? 'true' : 'false'); });
      }));
    });

    if (R.chips) {
      /* The visible-item count only becomes a live region once the shopper has
         actually filtered something — otherwise it re-announces on every boot
         pass while they are still reading the masthead. */
      on(R.chips, 'click', guard(function () {
        if (R.visible && !R.visible.getAttribute('aria-live')) { R.visible.setAttribute('aria-live', 'polite'); }
      }));
      /* The fade mask must lift when the rail is scrolled to its end, or the
         last chip is permanently unreadable. */
      var syncMask = guard(function () {
        var atEnd = (R.chips.scrollLeft + R.chips.clientWidth) >= (R.chips.scrollWidth - 1);
        R.chips.classList.toggle('is-ccw-scrollend', atEnd);
      });
      on(R.chips, 'scroll', syncMask, { passive: true });
      on(window, 'resize', syncMask);
      syncMask();
    }

    var addBtn = qs('[data-ccw-addselected]', R.root);
    if (addBtn) { on(addBtn, 'click', guard(addSelected)); }
    var clearBtn = qs('[data-ccw-clear-selection]', R.root);
    if (clearBtn) { on(clearBtn, 'click', guard(clearSelection)); }

    if (R.toast) {
      var x = qs('[data-ccw-toast-dismiss]', R.toast);
      if (x) { on(x, 'click', guard(hideToast)); }
      /* Timed content that contains a link must not expire under someone who
         is reaching for it. */
      on(R.toast, 'focusin', function () { if (toastTimer) { window.clearTimeout(toastTimer); toastTimer = null; } });
      on(R.toast, 'pointerenter', function () { if (toastTimer) { window.clearTimeout(toastTimer); toastTimer = null; } });
      on(R.toast, 'pointerleave', function () { armToast(4000); });
    }

    var copyBtn = qs('[data-ccw-copy]', R.root);
    if (copyBtn) {
      on(copyBtn, 'click', guard(function () {
        var lines = cards().map(function (c) {
          var v = variantTitle(c);
          return title(c) + (v && v !== 'Default Title' ? ' — ' + v : '') + ' — ' + money(priceCents(c));
        });
        var body = lines.join('\n');
        if (window.navigator && window.navigator.clipboard) {
          window.navigator.clipboard.writeText(body).then(function () { toast('List copied to your clipboard.'); }).catch(warn);
        }
      }));
    }
  }

  /* Existing behaviours preserved from the old custom JS box, correctly timed. */
  function legacyBits() {
    qsa('.iwishMsgSuccess', R.root).forEach(function (el) {
      if (el.__ccwFade) { return; }
      el.__ccwFade = true;
      window.setTimeout(function () { el.classList.add('ccw-fade'); }, 3000);
    });
    qsa('.iwishBuyBtn.disabled, .iwishBuyBtn[disabled]', R.root).forEach(function (b) {
      if (!/sold\s*out/i.test(b.textContent || '')) { b.textContent = 'Sold out'; }
      b.setAttribute('aria-disabled', 'true');
    });
  }

  /* Entry animation, exactly once per card node. Re-sorting must never replay
     it — four staggered replays in three seconds reads as a page that cannot
     finish loading. */
  function markNew() {
    cards().forEach(function (c) {
      if (c.__ccwAnimated) { return; }
      c.__ccwAnimated = true;
      c.classList.add('is-ccw-new');
      window.setTimeout(function () { c.classList.remove('is-ccw-new'); }, 1000);
    });
  }

  /* ================================================== EXTENSION REGISTRATION */
  /* Every entry point de-duplicates. Two <script src> tags for the same
     extension module, or a module registered from both a section and the
     layout, must not double-apply — and a half-de-duplicated API (ids checked
     on three hooks, function references on none) produces a confusing partial
     failure rather than an obvious one. */
  function dup(list, id, fn, label) {
    for (var i = 0; i < list.length; i++) {
      if (id && list[i].id === id) { warn(label + ': duplicate id ' + id); return true; }
      if (fn && list[i].fn === fn) { warn(label + ': duplicate function'); return true; }
    }
    return false;
  }

  /* Both the {id, fn} object form and a bare function are accepted — the bare
     form can only de-duplicate on the function reference, which is why the
     object form is the documented one. */
  function normalise(spec, fnKey) {
    if (typeof spec === 'function') { return { id: '', fn: spec }; }
    if (spec && typeof spec[fnKey] === 'function') { return { id: spec.id || '', fn: spec[fnKey] }; }
    return null;
  }

  function addStatCell(spec) {
    if (!spec || !spec.id || typeof spec.value !== 'function') { warn('addStatCell: bad spec'); return false; }
    if (dup(EXT.stats, spec.id, spec.value, 'addStatCell')) { return false; }
    EXT.stats.push({ id: spec.id, label: spec.label || '', order: typeof spec.order === 'number' ? spec.order : 500, value: spec.value, fn: spec.value });
    return true;
  }

  function addMastheadLine(spec) {
    if (!spec || !spec.id || typeof spec.render !== 'function') { warn('addMastheadLine: bad spec'); return false; }
    if (dup(EXT.lines, spec.id, spec.render, 'addMastheadLine')) { return false; }
    EXT.lines.push({ id: spec.id, order: typeof spec.order === 'number' ? spec.order : 50, render: spec.render, fn: spec.render });
    return true;
  }

  function addBadges(spec) {
    var n = normalise(spec, 'fn');
    if (!n) { warn('addBadges: bad spec'); return false; }
    if (dup(EXT.badges, n.id, n.fn, 'addBadges')) { return false; }
    EXT.badges.push(n);
    return true;
  }

  /* Spliced into the LIVE array at registration time. Concatenating per pass
     instead would move chip nodes between passes and blur them. */
  function addFilter(spec) {
    if (!spec || !spec.id || typeof spec.test !== 'function') { warn('addFilter: bad spec'); return false; }
    if (filterById(spec.id)) { warn('addFilter: duplicate id ' + spec.id); return false; }
    var order = typeof spec.order === 'number' ? spec.order : 500;
    var f = {
      id: spec.id,
      label: spec.label || spec.id,
      order: order,
      /* A throwing test must read as "no match" for that card, not abort the
         whole chip build. */
      test: function (c) { return !!safe(spec.test, [c], false); }
    };
    var i = 0;
    while (i < FILTERS.length && FILTERS[i].order <= order) { i++; }
    FILTERS.splice(i, 0, f);
    return true;
  }

  /* CONTRACT: fn(variants, product, card) returns the variants to SHOW. You
     may only REMOVE or REORDER — anything not present in product.variants is
     discarded, because posting an id the product does not have is a dead cart.
     The saved variant id is not passed, and the saved variant is not exempt:
     see visibleVariants(). */
  function filterVariants(spec) {
    var n = normalise(spec, 'fn');
    if (!n) { warn('filterVariants: bad spec'); return false; }
    if (dup(EXT.variants, n.id, n.fn, 'filterVariants')) { return false; }
    EXT.variants.push(n);
    return true;
  }

  function addEmptyLink(spec) {
    if (!spec || !spec.href || !spec.label) { warn('addEmptyLink: bad spec'); return false; }
    var href = String(spec.href);
    for (var i = 0; i < EXT.emptyLinks.length; i++) {
      if (EXT.emptyLinks[i].href === href) { warn('addEmptyLink: duplicate href ' + href); return false; }
    }
    EXT.emptyLinks.push({ href: href, label: String(spec.label), order: typeof spec.order === 'number' ? spec.order : 50 });
    return true;
  }

  function setCopy(k, s) {
    if (!Object.prototype.hasOwnProperty.call(COPY, k)) { warn('setCopy: unknown key ' + k); return false; }
    if (typeof s !== 'string' || !s) { warn('setCopy: empty value for ' + k); return false; }
    if (COPY[k] === s && COPY_SET[k]) { return false; }
    COPY[k] = s;
    COPY_SET[k] = true;
    return true;
  }

  var api = {
    version: 1,
    addStatCell: addStatCell,
    addMastheadLine: addMastheadLine,
    addBadges: addBadges,
    addFilter: addFilter,
    filterVariants: filterVariants,
    addEmptyLink: addEmptyLink,
    setCopy: setCopy,
    util: {
      qs: qs, qsa: qsa, on: on, attr: attr, text: text, warn: warn, guard: guard,
      store: store, money: money,
      root: function () { return R.root; },
      cards: cards,
      title: title, variantTitle: variantTitle,
      variantId: variantId, savedVariantId: savedVariantId,
      priceCents: priceCents, isSoldOut: isSoldOut,
      fetchProduct: fetchProduct
    }
  };

  /* Runs the callback immediately and synchronously. If it throws, whatever it
     registered BEFORE the throw stays live and everything after it is lost —
     logged, page unaffected. The same callback reference is never run twice,
     so a duplicated module load registers nothing new. */
  var registered = [];
  function register(fn) {
    if (typeof fn !== 'function') { return false; }
    for (var i = 0; i < registered.length; i++) { if (registered[i] === fn) { warn('extend: already registered'); return false; } }
    registered.push(fn);
    try { fn(api); } catch (e) { warn(e); }
    return true;
  }

  /* Everything a completed pass latches, and that a newly-registered hook has
     a right to change. Clearing these is what makes extend() order-independent
     instead of "must register before first render", which `defer` does not
     actually guarantee. */
  function invalidate() {
    enrichSig = '';
    if (R.links) { R.links.__ccwLinksDone = false; }
    if (R.accountBody) { R.accountBody.__ccwDone = false; }
    if (R.dispatch && !dispatchCommitted) { R.dispatch.__ccwRendered = false; }
    cards().forEach(function (c) {
      var slot = qs('[data-ccw-sizes]', c);
      /* Only the stamp is cleared. __ccwBuilt stays true so the built markup
         is not torn down until a pass actually rebuilds it. */
      if (slot) { slot.__ccwBuiltWith = -1; }
    });
  }

  var passTimer = null;
  function schedulePass() {
    if (passTimer) { window.clearTimeout(passTimer); }
    passTimer = window.setTimeout(function () {
      passTimer = null;
      guard(pass)();
      /* The dispatch line owns state the pass cannot reconstruct (the diff
         against the last snapshot), so it is replayed from the stashed
         changes rather than recomputed. */
      if (lastChanges) { guard(renderDispatch)(lastChanges); }
    }, 30);
  }

  NS.extend = function (fn) {
    if (!register(fn)) { return; }
    /* Registered after the first pass — the ordinary case for anything loaded
       async, and the case `defer` cannot be trusted to prevent. Wipe the
       latches and re-render. The pass writes to the grid only through
       applySort's withMute(), so the observer still cannot loop. */
    if (booted) { invalidate(); schedulePass(); }
  };
  pendingRegs.forEach(register);
  pendingRegs = null;

  /* ------------------------------------------------------------------- boot */
  function pass() {
    if (!refs()) { return; }
    /* Each step is guarded INDIVIDUALLY. One wrapper around the lot would mean
       a single missing element in the master template silently disables sort,
       filters, badges and enrichment all at once. */
    [
      /* Backstop, and the appRendered gate is the whole of it. A pass can be
         scheduled at any time — NS.extend() called at 2s runs one 30ms later,
         while the app is still fetching — and an ungated wipe there would
         return the page to a blank grid for another two seconds. Gated, any
         future path that sets appRendered inherits the cleanup for free. */
      function () { if (appRendered) { clearSkeletons(); } },
      renderCopy,
      function () { cards().forEach(function (c) { renderBadges(c); renderMeta(c); syncCheckbox(c); }); },
      markNew,
      legacyBits,
      /* Same slot the old hardcoded masthead line occupied: registered lines
         read cards(), so the grid has to be populated by now, and it is. */
      renderMastheadLines,
      buildChips,
      renderEmpty,
      refreshStats,
      function () { setFilter(activeFilter); },
      applySort,
      enrich,
      function () {
        /* Re-run the size switchers for any card whose product JSON is already
           memoised. enrich() short-circuits on an unchanged card set, so
           without this a pass scheduled by a late extension registration would
           never rebuild a switcher. */
        cards().forEach(function (c) {
          var product = PJ[attr(c, 'data-handle')];
          if (product) { guard(renderSizes)(c, product); }
        });
      },
      function () {
        /* List is the default above 20 items on wide screens — past that a card
           grid is just a scroll, and a long list needs price-comparability. */
        if (R.root.__ccwViewDone) { return; }
        R.root.__ccwViewDone = true;
        var view = store.get(K_VIEW, null);
        if (!view && cards().length > 20 && window.matchMedia && window.matchMedia('(min-width: 990px)').matches) { view = 'list'; }
        if (view) {
          R.root.setAttribute('data-view', view);
          qsa('[data-ccw-view]', R.root).forEach(function (o) {
            o.setAttribute('aria-pressed', o.getAttribute('data-ccw-view') === view ? 'true' : 'false');
          });
        }
      }
    ].forEach(function (f) { guard(f)(); });
  }

  var booted = false;

  function boot() {
    if (!refs()) { return; }
    if (!booted) {
      booted = true;

      /* ORDER IS LOAD-BEARING, TWICE OVER.
         (1) Before the observer below exists, so our own insertion cannot be
             read as the app's render — see paintSkeletons().
         (2) Paint first, network second: warmCache() only queues fetches, but
             queueing them ahead of the paint puts work between the shopper and
             the first frame that says the page is alive. */
      /* Parsed ONCE, here, and handed to both — see the note on skeletonCount
         for why this is a parameter and not a memo. guard() returns null on a
         throw, and an empty array is the right degenerate value: skeletonCount
         then falls through to its iWishCnt fallback exactly as it does today. */
      var saved = guard(savedIds)() || [];
      guard(paintSkeletons)(saved);
      guard(warmCache)(saved);

      guard(bindAddToCart)();
      guard(bindRemove)();
      guard(bindChrome)();

      /* Two observers, not one. The app writes its own empty-state markup into
         .sysMessage THREE SECONDS after a remove-to-zero — watching only the
         grid would let that land unstyled on top of a finished page.
         The muting flag is what stops our own sort writes re-triggering this
         and creating a permanent re-render loop. */
      if (window.MutationObserver) {
        mo = new window.MutationObserver(function () {
          if (muting) { try { mo.takeRecords(); } catch (e) { warn(e); } return; }
          appRendered = true;
          /* BEFORE the pass is scheduled, not merely "soon". This callback is
             a microtask that runs after the app's write and before the next
             paint, so removing here is what guarantees skeletons and real
             cards never share a frame — and what keeps a stray skeleton out of
             the :nth-child stagger and out of applySort's ordering check. Do
             not reorder these two statements. */
          clearSkeletons();
          if (moTimer) { window.clearTimeout(moTimer); }
          moTimer = window.setTimeout(pass, 80);
        });
        if (R.grid) { mo.observe(R.grid, { childList: true }); }
        if (R.sys) { mo.observe(R.sys, { childList: true, subtree: true }); }
      }

      /* If the app never calls its hook and never writes the grid, we still
         have to show SOMETHING rather than a masthead over a void. A total app
         failure must land on the empty state, not on a wall of grey bars
         pretending to be a list forever — hence the clear here. */
      window.setTimeout(function () { appRendered = true; clearSkeletons(); pass(); }, 6000);
    }
    pass();
  }

  /* The app's own hook: fires after the initial render and after every
     pagination render, including its error path. Chain rather than clobber, in
     case anything else in the theme has claimed it. */
  var prevHook = window.iWishPageinitFn;
  window.iWishPageinitFn = function () {
    if (typeof prevHook === 'function') { try { prevHook.apply(this, arguments); } catch (e) { warn(e); } }
    appRendered = true;
    /* The no-MutationObserver path. boot() -> pass() would clear them a step
       later via the gated backstop, but that is one guarded step further from
       the render than it needs to be. */
    clearSkeletons();
    boot();
  };

  /* THE ONE THING NOT TO "TIDY" (header note 7).
     There is no `else { boot(); }` here on purpose. A classic defer'd script
     executes with readyState === 'interactive' — never 'loading' — so an else
     branch would run the first full pass at the bottom of THIS file, before
     the browser has executed the store's extension module. On a warm
     sessionStorage cache fetchProduct resolves synchronously, so that pass
     would build every size switcher with zero registered filters and latch it.
     DOMContentLoaded is guaranteed to be after every defer'd script in the
     document; 'complete' is the only state where waiting is pointless.
     Later sweeps stay as a backstop for an app that renders late. */
  if (D.readyState === 'complete') { boot(); }
  else { on(D, 'DOMContentLoaded', boot); }
  on(window, 'load', boot);
  window.setTimeout(boot, 1200);
  window.setTimeout(boot, 3000);
}());
